/* eslint-disable react-refresh/only-export-components */
/**
 * CommandPalette — atalho ⌘K (Cmd+K / Ctrl+K) Linear-style.
 *
 * Apresenta lista de ações filtradas por role e estado (ex.: "Aprovar próxima
 * pendente" só aparece pra TEAMAdmin com ≥1 pending).
 *
 * Implementação:
 *  - Lib: cmdk (Vercel, ~14kb gzip)
 *  - Estilo: tokens Aura DS v3 (sem CSS default do cmdk)
 *  - A11y: trap de foco nativo, ESC fecha, role="dialog"
 *  - Atalho: registra handler global (⌘K / Ctrl+K)
 */

import { Command } from 'cmdk';
import {
  ArrowRight,
  CheckSquare,
  Gauge,
  House,
  ListChecks,
  MagnifyingGlass,
  Plus,
  Shield,
  SignOut,
  UsersThree,
} from '@phosphor-icons/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';

import { isApprover, isAuditor, isClientAdmin, jitApi } from '@/lib/api';
import { useSession } from '@/lib/auth/useSession';
import { cn } from '@/lib/utils/cn';

interface PaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

/**
 * Hook pra abrir/fechar a palette de qualquer lugar.
 *
 * @example
 *   const { setOpen } = useCommandPalette();
 *   <Button onClick={() => setOpen(true)}>Buscar (⌘K)</Button>
 */
export function useCommandPalette() {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within <CommandPalette>');
  }
  return ctx;
}

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  shortcut?: string;
  group: string;
  icon: typeof Plus;
  action: () => void | Promise<void>;
}

interface CommandPaletteProps {
  children: import('react').ReactNode;
}

/**
 * Provider + render do command palette. Coloque no AppShell.
 */
export function CommandPalette({ children }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [clients, setClients] = useState<{ slug: string; name: string }[]>([]);

  const { user, signOut } = useSession();
  const navigate = useNavigate();

  const userIsApprover = isApprover(user?.groups);
  const userIsAuditor = isAuditor(user?.groups);
  const userIsClientAdmin = isClientAdmin(user?.groups);

  // Atalho global ⌘K / Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lazy load de contexto quando palette abre (não bloqueia mount)
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();

    if (userIsApprover && pendingCount === null) {
      jitApi
        .listPendingApprovals({ limit: 1 }, ctrl.signal)
        .then((r) => setPendingCount(r.items.length))
        .catch(() => setPendingCount(0));
    }

    if (userIsClientAdmin && clients.length === 0) {
      jitApi
        .listClients(ctrl.signal)
        .then((r) => setClients(r.items.map((c) => ({ slug: c.slug, name: c.name }))))
        .catch(() => undefined);
    }

    return () => ctrl.abort();
  }, [open, userIsApprover, userIsClientAdmin, pendingCount, clients.length]);

  const run = useCallback((fn: () => void | Promise<void>) => {
    return async () => {
      setOpen(false);
      await fn();
    };
  }, []);

  const items: CommandItem[] = useMemo(() => {
    const out: CommandItem[] = [];

    if (userIsApprover && pendingCount && pendingCount > 0) {
      out.push({
        id: 'approve-next',
        label: `Aprovar próxima pendente (${pendingCount})`,
        hint: 'Ir para aprovações',
        group: 'Ações',
        icon: CheckSquare,
        action: () => navigate('/approvals'),
      });
    }

    // Ir para
    out.push(
      {
        id: 'goto-home',
        label: 'Painel',
        shortcut: 'G H',
        group: 'Ir para',
        icon: House,
        action: () => navigate('/'),
      },
      {
        id: 'goto-requests',
        label: 'Minhas solicitações',
        shortcut: 'G R',
        group: 'Ir para',
        icon: ListChecks,
        action: () => navigate('/requests'),
      },
    );

    if (userIsApprover) {
      out.push({
        id: 'goto-approvals',
        label: 'Fila de aprovação',
        shortcut: 'G A',
        group: 'Ir para',
        icon: CheckSquare,
        action: () => navigate('/approvals'),
      });
    }
    if (userIsAuditor) {
      out.push({
        id: 'goto-audit',
        label: 'Logs',
        group: 'Ir para',
        icon: Gauge,
        action: () => navigate('/audit'),
      });
    }
    if (userIsClientAdmin) {
      out.push({
        id: 'goto-admin',
        label: 'Gerenciar clientes',
        group: 'Ir para',
        icon: UsersThree,
        action: () => navigate('/admin/clients'),
      });
      out.push({
        id: 'new-client',
        label: 'Novo cliente',
        group: 'Ações',
        icon: Plus,
        action: () => navigate('/admin/clients/new'),
      });
    }

    // Clientes (so admin pode navegar pra detail; Home cobre user normal)
    if (userIsClientAdmin) {
      for (const c of clients) {
        out.push({
          id: `client-${c.slug}`,
          label: c.name,
          hint: `/clients/${c.slug}`,
          group: 'Clientes',
          icon: Shield,
          action: () => navigate(`/clients/${c.slug}`),
        });
      }
    }

    // Conta
    out.push({
      id: 'sign-out',
      label: 'Sair',
      group: 'Conta',
      icon: SignOut,
      action: async () => {
        await signOut();
        navigate('/login', { replace: true });
      },
    });

    return out;
  }, [navigate, signOut, userIsApprover, userIsAuditor, userIsClientAdmin, pendingCount, clients]);

  // Agrupa por group preservando ordem
  const grouped = useMemo(() => {
    const groups = new Map<string, CommandItem[]>();
    for (const it of items) {
      if (!groups.has(it.group)) groups.set(it.group, []);
      groups.get(it.group)!.push(it);
    }
    return Array.from(groups.entries());
  }, [items]);

  const ctx = useMemo<PaletteContextValue>(
    () => ({ open, setOpen, toggle: () => setOpen((p) => !p) }),
    [open],
  );

  return (
    <PaletteContext.Provider value={ctx}>
      {children}

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center bg-navy/40 px-4 pt-[12vh] backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: [0.2, 0.6, 0.2, 1] }}
            onClick={() => setOpen(false)}
            role="presentation"
          >
            <motion.div
              className="w-full max-w-xl"
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.2, 0.6, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Buscar ações"
            >
              <Command
                label="Buscar ações"
                className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-overlay"
              >
                <div className="flex items-center gap-3 border-b border-border px-4">
                  <MagnifyingGlass
                    size={16}
                    weight="regular"
                    className="text-muted"
                    aria-hidden="true"
                  />
                  <Command.Input
                    placeholder="Buscar ou executar…"
                    className={cn(
                      'flex-1 border-0 bg-transparent py-3.5 text-sm text-ink outline-none',
                      'placeholder:text-muted focus:outline-none focus:ring-0',
                    )}
                  />
                  <kbd className="hidden rounded-sm border border-border bg-surface-muted px-1.5 py-0.5 font-mono text-[0.625rem] text-muted sm:inline-block">
                    ESC
                  </kbd>
                </div>

                <Command.List className="max-h-[60vh] overflow-y-auto p-2">
                  <Command.Empty className="px-3 py-6 text-center text-sm text-muted">
                    Sem resultados.
                  </Command.Empty>

                  {grouped.map(([group, list]) => (
                    <Command.Group
                      key={group}
                      heading={
                        <span className="px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
                          {group}
                        </span>
                      }
                    >
                      {list.map((it) => (
                        <Command.Item
                          key={it.id}
                          value={`${group} ${it.label} ${it.hint ?? ''}`}
                          onSelect={() => void run(it.action)()}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-ink',
                            'aria-selected:bg-surface-muted aria-selected:text-ink',
                            'data-[selected=true]:bg-surface-muted',
                          )}
                        >
                          <it.icon
                            size={16}
                            weight="regular"
                            className="text-muted"
                            aria-hidden="true"
                          />
                          <span className="flex-1 truncate">{it.label}</span>
                          {it.hint ? (
                            <span className="truncate text-xs text-muted">{it.hint}</span>
                          ) : null}
                          {it.shortcut ? (
                            <kbd className="rounded-sm border border-border bg-surface-muted px-1.5 py-0.5 font-mono text-[0.625rem] text-muted">
                              {it.shortcut}
                            </kbd>
                          ) : (
                            <ArrowRight
                              size={12}
                              weight="bold"
                              className="text-muted opacity-0 group-aria-selected:opacity-100"
                              aria-hidden="true"
                            />
                          )}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ))}
                </Command.List>
              </Command>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PaletteContext.Provider>
  );
}
